import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from "react";
import { useWindowDimensions } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { View } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { useCameraSearchSort } from "@src/features/cameras";
import { useTenant } from "@src/features/tenants";
import type { SortSelection } from "@src/features/clips/types/camera";
import { Sidepanel } from "./Sidepanel.web";
import { WebHeader } from "./WebHeader.web";
import { TenantSwitcherPanel } from "./TenantSwitcherPanel.web";

const SIDEBAR_COLLAPSED_KEY = "@web_sidebar_collapsed";

interface TenantSwitcherControl {
  open: () => void;
}

const TenantSwitcherContext = createContext<TenantSwitcherControl | null>(null);

export function useTenantSwitcherControl(): TenantSwitcherControl | null {
  return useContext(TenantSwitcherContext);
}

interface WebLayoutProps {
  children?: ReactNode;
  rightPanel?: ReactNode;
  searchQuery?: string;
  sortBy?: SortSelection;
}

export function WebLayout({ children, rightPanel, searchQuery: searchQueryProp, sortBy: sortByProp }: WebLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [tenantSwitcherVisible, setTenantSwitcherVisible] = useState(false);
  const { searchQuery, sortBy } = useCameraSearchSort();
  const { tenants } = useTenant();
  const query = searchQueryProp ?? searchQuery;
  const sort = sortByProp ?? sortBy;
  const hasMultipleTenants = tenants.length > 1;

  useEffect(() => {
    AsyncStorage.getItem(SIDEBAR_COLLAPSED_KEY).then((value) => {
      if (value !== null) {
        setCollapsed(value === "true");
      }
    });
  }, []);

  const handleToggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const newValue = !prev;
      AsyncStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue));
      return newValue;
    });
  }, []);

  const handleOpenTenantSwitcher = useCallback(() => {
    setTenantSwitcherVisible(true);
  }, []);

  const handleCloseTenantSwitcher = useCallback(() => {
    setTenantSwitcherVisible(false);
  }, []);

  const switcherControl = hasMultipleTenants ? { open: handleOpenTenantSwitcher } : null;

  return (
    <TenantSwitcherContext.Provider value={switcherControl}>
      <View flex={1} flexDirection="row" bg="background" width="100%" height="100%" p="3xl" gap="3xl">
        <Sidepanel
          collapsed={collapsed}
          onToggleCollapse={handleToggleCollapse}
          searchQuery={query}
          sortBy={sort}
          onOpenTenantSwitcher={hasMultipleTenants ? handleOpenTenantSwitcher : undefined}
        />

        <View flex={1} height="100%">
          <WebHeader />

          <View flex={1} flexDirection="row" gap="3xl">
            <View flex={1} bg="background">
              {children}
            </View>

            {rightPanel && (
              <View
                width={sizes.webRightPanelWidth}
                borderLeftWidth={0.5}
                borderColor="borderCard"
                pl="3xl"
                pr="3xl"
                pt="8xl"
                pb="3xl"
              >
                {rightPanel}
              </View>
            )}
          </View>
        </View>
      </View>

      {hasMultipleTenants && (
        <TenantSwitcherPanel visible={tenantSwitcherVisible} onClose={handleCloseTenantSwitcher} />
      )}
    </TenantSwitcherContext.Provider>
  );
}

export function useWebLayout(): boolean {
  const { width: windowWidth } = useWindowDimensions();
  return windowWidth > sizes.webLayoutBreakpoint;
}
